import { useCallback, useEffect, useRef, useState } from 'react'
import {
  VStack,
  HStack,
  Stack,
  Text,
  Badge,
  Box,
  Button,
  Collapse,
  IconButton,
  Textarea,
  Spinner,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import {
  Storefront,
  ArrowsClockwise,
  Copy,
  Warning,
  Plus,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react'
import api from '../../services/api'

interface PedidoStatusCanal {
  numeroWeDrop: number
  numeroNoCanal: string | null
  canal: string
  consultaDireta: boolean
  statusWeDrop: string
  statusWeDropRotulo: string
  saidaDoCdEm: string | null
  statusCanal: string | null
  statusCanalRotulo: string | null
  explicacao: string
  divergencia: boolean
  orientacao: string | null
  textoCliente: string
  wms: {
    wmsId: number
    situacao: string
    rotulo: string
    separadoEm: string | null
    embaladoEm: string | null
    expedidoEm: string | null
    divergencia: boolean
    alerta: string | null
  } | null
}

interface StatusCanalResposta {
  pedidos: PedidoStatusCanal[]
  naoEncontrados: string[]
  naoConsultados: string[]
  resumo: string
  consultadoEm: string
  aviso?: string
}

async function copiarTexto(texto: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(texto)
    return
  } catch {
    // http sem clipboard API: cai no textarea
  }
  const el = document.createElement('textarea')
  el.value = texto
  el.style.position = 'fixed'
  el.style.opacity = '0'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

/**
 * Status dos pedidos do ticket na WeDrop × no canal (ML/Shopee), embutido no
 * topo da conversa: consulta sozinho ao abrir o ticket; o botão só recheca.
 */
export default function ChannelStatusPanel({ ticketId }: { ticketId: number }) {
  const toast = useToast()
  const [referencias, setReferencias] = useState('')
  const [mostrarMais, setMostrarMais] = useState(false)
  const [aberto, setAberto] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [dados, setDados] = useState<StatusCanalResposta | null>(null)
  /** Resposta de um ticket anterior que chega depois da troca não sobrescreve. */
  const ticketAtual = useRef(ticketId)

  const cardBg = useColorModeValue('gray.50', '#201f23')
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.100')
  const divergeBg = useColorModeValue('orange.50', 'rgba(237, 137, 54, 0.08)')
  const divergeBorder = useColorModeValue('orange.300', 'orange.400')
  const colBg = useColorModeValue('white', 'whiteAlpha.50')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const orientacaoColor = useColorModeValue('orange.700', 'orange.200')

  const consultar = useCallback(
    async (refs: string) => {
      const alvo = ticketId
      setIsLoading(true)
      setErro(null)
      try {
        const response = await api.post(
          `/internal-tickets/${alvo}/channel-status`,
          {
            referencias: refs,
          },
        )
        if (ticketAtual.current === alvo) setDados(response.data)
      } catch (error: any) {
        if (ticketAtual.current === alvo) {
          setErro(
            error.response?.data?.error ||
              'Não foi possível consultar agora. Tente de novo.',
          )
        }
      } finally {
        if (ticketAtual.current === alvo) setIsLoading(false)
      }
    },
    [ticketId],
  )

  // Abriu (ou trocou de) ticket: limpa e consulta na hora
  useEffect(() => {
    ticketAtual.current = ticketId
    setReferencias('')
    setMostrarMais(false)
    setDados(null)
    setErro(null)
    setAberto(true)
    consultar('')
  }, [ticketId, consultar])

  const copiarResumo = async () => {
    if (!dados?.resumo) return
    try {
      await copiarTexto(dados.resumo)
      toast({ title: 'Resumo copiado', status: 'success', duration: 2000 })
    } catch {
      toast({
        title: 'Não foi possível copiar',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const divergentes = dados?.pedidos.filter((p) => p.divergencia).length ?? 0
  // Nada para mostrar (ticket sem pedido do cliente): some, sem ocupar espaço
  if (
    dados &&
    dados.pedidos.length === 0 &&
    !dados.aviso &&
    !erro &&
    !mostrarMais
  ) {
    return (
      <HStack justify="flex-end">
        <Button
          size="xs"
          variant="ghost"
          colorScheme="yellow"
          leftIcon={<Plus size={12} />}
          onClick={() => setMostrarMais(true)}
        >
          Consultar pedidos no canal
        </Button>
      </HStack>
    )
  }

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={divergentes ? divergeBorder : cardBorder}
      borderRadius="10px"
      p={{ base: '0.75rem', md: '1rem' }}
      flexShrink={0}
    >
      {/* Cabeçalho: resumo + ações */}
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', sm: 'center' }}
        spacing={2}
      >
        <HStack spacing={2} flexWrap="wrap" minW={0}>
          <Storefront size={18} color="#ECC94B" />
          <Text
            color={textPrimary}
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="semibold"
          >
            Status no canal
          </Text>
          {isLoading && <Spinner size="xs" color="yellow.400" />}
          {dados && dados.pedidos.length > 0 && (
            <Badge colorScheme="gray" fontSize="2xs">
              {dados.pedidos.length} pedido
              {dados.pedidos.length === 1 ? '' : 's'}
            </Badge>
          )}
          {divergentes > 0 && (
            <Badge
              colorScheme="orange"
              fontSize="2xs"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Warning size={12} weight="fill" /> {divergentes} com divergência
            </Badge>
          )}
          {dados && !isLoading && (
            <Text fontSize="2xs" color={textMuted}>
              às{' '}
              {new Date(dados.consultadoEm).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          )}
        </HStack>
        <HStack
          spacing={1}
          justify={{ base: 'flex-start', sm: 'flex-end' }}
          flexWrap="wrap"
        >
          <Button
            size="xs"
            variant="outline"
            colorScheme="yellow"
            leftIcon={<ArrowsClockwise size={12} />}
            onClick={() => consultar(referencias)}
            isLoading={isLoading}
          >
            Rechecar
          </Button>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="yellow"
            leftIcon={<Plus size={12} />}
            onClick={() => setMostrarMais((v) => !v)}
          >
            Mais pedidos
          </Button>
          {dados && dados.pedidos.length > 0 && (
            <Button
              size="xs"
              variant="ghost"
              colorScheme="yellow"
              leftIcon={<Copy size={12} />}
              onClick={copiarResumo}
            >
              Copiar resumo
            </Button>
          )}
          <IconButton
            aria-label={aberto ? 'Recolher' : 'Expandir'}
            icon={aberto ? <CaretUp size={14} /> : <CaretDown size={14} />}
            size="xs"
            variant="ghost"
            onClick={() => setAberto((v) => !v)}
          />
        </HStack>
      </Stack>

      <Collapse in={mostrarMais} animateOpacity>
        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={2}
          align={{ base: 'stretch', sm: 'flex-end' }}
          mt={2}
        >
          <Textarea
            value={referencias}
            onChange={(e) => setReferencias(e.target.value)}
            placeholder="Outros números (WeDrop, do canal, pack ou rastreio), separados por vírgula, espaço ou linha"
            size="sm"
            rows={2}
            resize="vertical"
            fontSize={{ base: 'xs', md: 'sm' }}
          />
          <Button
            size="sm"
            colorScheme="yellow"
            onClick={() => consultar(referencias)}
            isLoading={isLoading}
            flexShrink={0}
          >
            Consultar
          </Button>
        </Stack>
      </Collapse>

      <Collapse in={aberto} animateOpacity>
        <VStack spacing={2} align="stretch" mt={2}>
          {isLoading && !dados && (
            <Text fontSize="xs" color={textMuted}>
              Consultando os canais...
            </Text>
          )}
          {erro && (
            <Text fontSize="xs" color="red.300">
              {erro}
            </Text>
          )}
          {dados?.aviso && (
            <Text fontSize="xs" color={orientacaoColor}>
              {dados.aviso}
            </Text>
          )}

          {dados && dados.pedidos.length > 0 && (
            <TableContainer
              border="1px solid"
              borderColor={cardBorder}
              borderRadius="8px"
              whiteSpace="normal"
            >
              <Table size="sm" variant="simple">
                <Thead bg={colBg}>
                  <Tr>
                    {[
                      'Pedido',
                      'Na WeDrop',
                      'No WMS',
                      'No canal',
                      'Situação',
                    ].map((h) => (
                      <Th
                        key={h}
                        color={textMuted}
                        fontSize="2xs"
                        borderColor={cardBorder}
                        px={2}
                        py={2}
                      >
                        {h}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {dados.pedidos.map((p) => (
                    <Tr
                      key={p.numeroWeDrop}
                      bg={p.divergencia ? divergeBg : undefined}
                      boxShadow={
                        p.divergencia ? 'inset 4px 0 0 0 #ED8936' : undefined
                      }
                      verticalAlign="top"
                    >
                      <Td borderColor={cardBorder} px={2} py={2} minW="130px">
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={textPrimary}
                          wordBreak="break-all"
                        >
                          {p.numeroNoCanal || `#${p.numeroWeDrop}`}
                        </Text>
                        <HStack spacing={1} mt={1} flexWrap="wrap">
                          <Badge
                            fontSize="2xs"
                            colorScheme={p.consultaDireta ? 'blue' : 'gray'}
                          >
                            {p.canal}
                          </Badge>
                          <Text fontSize="2xs" color={textMuted}>
                            #{p.numeroWeDrop}
                          </Text>
                        </HStack>
                        {p.divergencia && (
                          <Badge
                            mt={1}
                            colorScheme="orange"
                            fontSize="2xs"
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Warning size={10} weight="fill" /> Divergência
                          </Badge>
                        )}
                      </Td>
                      <Td borderColor={cardBorder} px={2} py={2} minW="110px">
                        <Text
                          fontSize="xs"
                          color={textPrimary}
                          fontWeight="semibold"
                        >
                          {p.statusWeDropRotulo}
                        </Text>
                        {p.saidaDoCdEm && (
                          <Text fontSize="2xs" color={textMuted}>
                            Saiu do CD {p.saidaDoCdEm}
                          </Text>
                        )}
                      </Td>
                      <Td borderColor={cardBorder} px={2} py={2} minW="120px">
                        {p.wms ? (
                          <>
                            <Text
                              fontSize="xs"
                              color={textPrimary}
                              fontWeight="semibold"
                            >
                              {p.wms.rotulo}
                            </Text>
                            <Text
                              fontSize="2xs"
                              color={textMuted}
                              fontFamily="mono"
                            >
                              {p.wms.situacao} · #{p.wms.wmsId}
                            </Text>
                            {[
                              ['Separado', p.wms.separadoEm],
                              ['Embalado', p.wms.embaladoEm],
                              ['Expedido', p.wms.expedidoEm],
                            ]
                              .filter(([, v]) => v)
                              .map(([k, v]) => (
                                <Text key={k} fontSize="2xs" color={textMuted}>
                                  {k} {v}
                                </Text>
                              ))}
                          </>
                        ) : (
                          <Text
                            fontSize="xs"
                            color={textMuted}
                            fontStyle="italic"
                          >
                            Não foi ao WMS
                          </Text>
                        )}
                      </Td>
                      <Td borderColor={cardBorder} px={2} py={2} minW="140px">
                        {p.statusCanalRotulo ? (
                          <>
                            <Text
                              fontSize="xs"
                              color={textPrimary}
                              fontWeight="semibold"
                            >
                              {p.statusCanalRotulo}
                            </Text>
                            {p.statusCanal && (
                              <Text
                                fontSize="2xs"
                                color={textMuted}
                                fontFamily="mono"
                                wordBreak="break-all"
                              >
                                {p.statusCanal}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text
                            fontSize="xs"
                            color={textMuted}
                            fontStyle="italic"
                          >
                            {p.consultaDireta
                              ? 'Não consultado agora'
                              : 'Sem consulta direta'}
                          </Text>
                        )}
                      </Td>
                      <Td borderColor={cardBorder} px={2} py={2} minW="220px">
                        <Text fontSize="xs" color={textSecondary}>
                          {p.explicacao}
                        </Text>
                        {p.orientacao && (
                          <Text fontSize="2xs" color={orientacaoColor} mt={1}>
                            Para a equipe: {p.orientacao}
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          {dados && dados.naoEncontrados.length > 0 && (
            <Text fontSize="xs" color={textMuted}>
              Não encontrados nesta conta: {dados.naoEncontrados.join(', ')}
            </Text>
          )}
          {dados && dados.naoConsultados.length > 0 && (
            <Text fontSize="xs" color={orientacaoColor}>
              Limite de {dados.pedidos.length} pedidos por consulta. Não
              consultados: {dados.naoConsultados.join(', ')}
            </Text>
          )}
        </VStack>
      </Collapse>
    </Box>
  )
}
